import { MdFileUpload, MdDescription, MdTableChart, MdPictureAsPdf, MdClose, MdDownload } from "react-icons/md";
import Card from "components/card";
import React, { useRef, useState, useEffect } from "react";
import { API_BASE_URL } from "utils/api";

const ACCEPTED = ".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.csv,.png,.jpg,.jpeg,.gif,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.spreadsheetml.macro-enabled.sheet,text/csv,image/png,image/jpeg,image/gif,image/webp";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB max for uploads

const formatBytes = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getFileNameFromDoc = (doc) => {
  if (!doc) return "Document";
  if (typeof doc === "string") return doc.split("/").pop();
  return (
    doc.name || doc.filename || doc.fileName || doc.url?.split("/").pop() || "Document"
  );
};

const getFileUrlFromDoc = (doc) => {
  if (!doc) return null;
  if (typeof doc === "string") {
    return /^https?:\/\//.test(doc) ? doc : `${API_BASE_URL}${doc.startsWith("/") ? doc : `/${doc}`}`;
  }
  if (doc.url) {
    return /^https?:\/\//.test(doc.url)
      ? doc.url
      : `${API_BASE_URL}${doc.url.startsWith("/") ? doc.url : `/${doc.url}`}`;
  }
  return null;
};

const normalizeDocuments = (docs) => {
  if (!Array.isArray(docs)) return [];
  return docs.map((doc, index) => {
    const name = getFileNameFromDoc(doc);
    const url = getFileUrlFromDoc(doc);
    return {
      id: typeof doc === "string" ? `${doc}-${index}` : doc._id || `${name}-${index}`,
      name,
      size: doc?.size || 0,
      url,
      raw: doc,
    };
  });
};

const mergeDocumentArrays = (prevDocs = [], docs = []) => {
  const merged = [...(prevDocs || []), ...(docs || [])];
  const unique = new Map();
  merged.forEach((doc) => {
    const key = getFileNameFromDoc(doc);
    if (!unique.has(key)) {
      unique.set(key, doc);
    }
  });
  return Array.from(unique.values());
};

const FileIcon = ({ name }) => {
  const ext = name.split(".").pop().toLowerCase();
  if (ext === "pdf")
    return (
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 dark:bg-red-900/20">
        <MdPictureAsPdf className="h-5 w-5 text-red-500" />
      </span>
    );
  if (["xls", "xlsx", "xlsm", "csv"].includes(ext))
    return (
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50 dark:bg-green-900/20">
        <MdTableChart className="h-5 w-5 text-green-600" />
      </span>
    );
  return (
    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20">
      <MdDescription className="h-5 w-5 text-blue-500" />
    </span>
  );
};

const Upload = ({ client, setClient }) => {
  const clientId = client?._id;
  const inputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const listRef = useRef(null);
  const pendingSectionRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, doc: null });
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const existingDocuments = normalizeDocuments(client?.documents);

  // Layout constants for documents grid: 2 columns, 4 rows visible before scroll
  const DOC_ITEM_HEIGHT_PX = 64; // h-16
  const VISIBLE_ROWS = 4;
  const GRID_GAP_PX = 12; // approximates gap-3
  const computedDocumentsMaxHeight = VISIBLE_ROWS * DOC_ITEM_HEIGHT_PX + (VISIBLE_ROWS - 1) * GRID_GAP_PX;

  // Note: scroll container uses flex-1 to fill remaining card height

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: "", type: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [toast.show]);

  useEffect(() => {
    if (files.length > 0) {
      // Scroll the documents list to its bottom and ensure the pending section is visible
      setTimeout(() => {
        try {
          if (listRef.current) {
            listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
          }
        } catch (e) {
          // ignore
        }

        try {
          if (pendingSectionRef.current) {
            // scroll the outer container so pending uploads are visible
            pendingSectionRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
          } else if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: "smooth" });
          }
        } catch (e) {
          // ignore
        }
      }, 120);
    }
  }, [files.length]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  const validateFileAsync = async (file) => {
    const name = file.name || "unnamed";
    const ext = (name.split(".").pop() || "").toLowerCase();

    const allowedExt = [
      "pdf",
      "doc",
      "docx",
      "xls",
      "xlsx",
      "xlsm",
      "csv",
      "png",
      "jpg",
      "jpeg",
      "gif",
      "webp",
    ];

    const allowedMimeTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.macro-enabled.sheet",
      "text/csv",
      "image/png",
      "image/jpeg",
      "image/gif",
      "image/webp",
    ];

    const isValidExt = allowedExt.includes(ext);
    const isValidMime = allowedMimeTypes.includes(file.type);

    if (!isValidExt && !isValidMime) {
      showToast(`${name} is not a supported format`, "error");
      return false;
    }

    let size = file.size || 0;

    if (size === 0 && typeof file.arrayBuffer === "function") {
      try {
        const buffer = await file.arrayBuffer();
        size = buffer.byteLength || 0;
      } catch (err) {
        console.warn("Failed to determine file size from blob:", err);
      }
    }

    if (size === 0) {
      showToast(`${name} appears empty or is not accessible`, "error");
      return false;
    }

    if (size > MAX_FILE_SIZE) {
      showToast(`${name} exceeds ${formatBytes(MAX_FILE_SIZE)} limit`, "error");
      return false;
    }

    return true;
  };

  const handleFiles = async (incoming) => {
    const newFiles = [];

    for (const file of Array.from(incoming)) {
      const ok = await validateFileAsync(file);
      if (!ok) continue;

      const size = file.size || 0;
      const exists = files.some(
        (f) => f.name === file.name && f.size === size
      );

      if (!exists) {
        newFiles.push({
          file,
          name: file.name,
          size,
          id: `${file.name}-${size}-${Date.now()}`,
        });
      }
    }

    if (newFiles.length) setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleChange = async (e) => {
    if (e.target.files?.length) {
      await handleFiles(e.target.files);
      e.target.value = "";
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) await handleFiles(e.dataTransfer.files);
  };

  const handleRemove = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpload = async () => {
    if (!files.length || !clientId) return;

    try {
      setUploading(true);
      const token = localStorage.getItem("token");

      const formData = new FormData();
      formData.append("name", client?.name);
      formData.append("email", client?.email);
      formData.append("phone", client?.phone);

      files.forEach((f) => {
        formData.append("documents", f.file);
      });

      const requestUrl = `${API_BASE_URL}/clients/${clientId}/documents`;
      const requestInit = {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      };

      const response = await fetch(requestUrl, requestInit);

      const responseText = await response.text();
      let responseData;
      try {
        responseData = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        responseData = { text: responseText };
      }

      if (!response.ok) {
        const errorMessage = responseData?.message || responseData?.error || "Upload failed";
        throw new Error(errorMessage);
      }

      const incomingDocuments = responseData?.data?.documents || [];

      if (setClient) {
        setClient((prev) => ({
          ...prev,
          documents: mergeDocumentArrays(prev?.documents, incomingDocuments),
        }));
      }

      setFiles([]);
      showToast("Documents uploaded successfully", "success");

      // If the documents list is scrollable, scroll to show the newly uploaded items
      setTimeout(() => {
        try {
          if (listRef.current) {
            listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
          }
        } catch (e) {
          // ignore scrolling errors
        }
      }, 120);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const openDeleteConfirm = (doc) => {
    setDeleteConfirm({ show: true, doc });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({ show: false, doc: null });
  };

  const handleDeleteDocument = async (doc) => {
    const fileName = getFileNameFromDoc(doc.raw || doc);
    if (!clientId || !fileName) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_BASE_URL}/clients/${clientId}/documents`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ fileName }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || "Failed to delete document");
      }

      if (setClient) {
        setClient((prev) => ({
          ...prev,
          documents: (prev?.documents || []).filter((item) => getFileNameFromDoc(item) !== fileName),
        }));
      }

      closeDeleteConfirm();
      showToast("Document deleted successfully", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Delete failed", "error");
    } finally {
      setDeleting(false);
    }
  };

  const handlePreviewDocument = (doc) => {
    const url = doc.url || getFileUrlFromDoc(doc.raw || doc);
    if (!url) {
      showToast("Preview not available for this document", "error");
      return;
    }
    window.open(url, "_blank");
  };

  return (
    <Card extra="w-full h-full p-4 flex flex-col gap-3 overflow-hidden">
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        className="flex flex-col items-center justify-center rounded-xl border-[2px] border-dashed border-gray-200 bg-blue-50 py-5 dark:border-navy-700 dark:bg-navy-700"
      >
        <MdFileUpload className="text-[48px] text-brand-500 dark:text-white" />
        <h4 className="mt-1 text-sm font-bold text-brand-500 dark:text-white">
          Upload Documents
        </h4>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED}
          className="hidden"
          onChange={handleChange}
          capture={false}
        />

        <button
          onClick={() => inputRef.current?.click()}
          className="mt-3 flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          <MdFileUpload className="h-4 w-4" />
          Choose Files
        </button>

        {files.length > 0 && (
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="mt-2 rounded-xl bg-green-500 px-4 py-1.5 text-sm text-white hover:bg-green-600 disabled:opacity-50"
          >
            {uploading ? "Uploading..." : "Upload Documents"}
          </button>
        )}

        <p className="mt-2 text-center text-[11px] text-gray-500">
          PDF · Word · Excel · Images (JPG/PNG/GIF) (max 10MB)<br />
        </p>
        <p className="mt-1 text-center text-[11px] text-gray-500">
          Only allow 10 files to upload at a time.
        </p>

        {toast.show && (
          <div className={`mt-3 rounded-xl border px-3 py-2 text-sm font-medium ${toast.type === "success"
              ? "border-green-200 bg-green-50 text-green-800"
              : "border-red-200 bg-red-50 text-red-800"
            }`}>
            {toast.message}
          </div>
        )}

      </div>

      {(existingDocuments.length > 0 || files.length > 0) ? (
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto flex flex-col gap-4 pr-1"
        >
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-navy-700 dark:text-white">
              Uploaded Documents ({existingDocuments.length})
            </p>
            <p className="text-[11px] text-gray-500">2 items per row, 4 rows visible, remaining under scroll</p>
          </div>

          <ul ref={listRef} className="grid grid-cols-1 gap-3 sm:grid-cols-2" style={{ maxHeight: `${computedDocumentsMaxHeight}px`, overflowY: 'auto' }}>
            {existingDocuments.map((doc) => (
              <li
                key={doc.id}
                className="h-16 flex items-center gap-2 rounded-2xl border bg-white px-3 shadow-sm dark:border-white/10 dark:bg-navy-800"
              >
                <FileIcon name={doc.name} />
                <div className="min-w-0 flex-1">
                  <p title={doc.name} className="truncate text-sm font-medium text-navy-900 dark:text-white">
                    {doc.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {doc.size ? formatBytes(doc.size) : "Uploaded"}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handlePreviewDocument(doc)}
                    className="rounded-full p-1 text-gray-400 hover:text-blue-500 transition-colors"
                    title="Preview"
                  >
                    <MdDownload className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openDeleteConfirm(doc)}
                    disabled={deleting}
                    className="rounded-full p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                    title="Delete"
                  >
                    <MdClose className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}

          </ul>

          {files.length > 0 && (
            <div ref={pendingSectionRef} className="flex flex-col gap-2 border-t pt-3 dark:border-navy-600">
              <p className="text-xs font-semibold text-navy-700 dark:text-white">
                Pending Upload ({files.length})
              </p>
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {files.map((f) => (
                  <li
                    key={f.id}
                    className="h-16 flex items-center gap-2 rounded-2xl border-2 border-dashed bg-blue-50 px-3 dark:bg-blue-900/10 dark:border-blue-700"
                  >
                    <FileIcon name={f.name} />
                    <div className="min-w-0 flex-1">
                      <p title={f.name} className="truncate text-sm font-medium dark:text-white">
                        {f.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatBytes(f.size)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemove(f.id)}
                      className="rounded-full p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <MdClose className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-navy-800">
          <MdDescription className="h-8 w-8 text-gray-300" />
          <p className="text-sm text-gray-400">No documents attached</p>
        </div>
      )}

      {toast.show && (
        <div className={`fixed inset-x-4 bottom-4 z-50 rounded-xl border px-4 py-3 text-sm font-medium shadow-lg sm:inset-x-auto sm:right-4 ${toast.type === "success"
            ? "border-green-200 bg-green-50 text-green-800"
            : "border-red-200 bg-red-50 text-red-800"
          }`}>
          {toast.message}
        </div>
      )}

      {deleteConfirm.show && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={closeDeleteConfirm}>
          <div
            className="w-full max-w-md overflow-hidden rounded-3xl bg-white p-6 shadow-2xl dark:bg-navy-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 text-red-600 dark:bg-red-500/10 dark:text-red-200">
                <MdClose className="h-6 w-6" />
              </div>
              <div>
                <p className="text-lg font-semibold text-navy-900 dark:text-white">Delete document</p>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  Are you sure you want to delete <span className="font-semibold">{getFileNameFromDoc(deleteConfirm.doc?.raw || deleteConfirm.doc)}</span>? This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDeleteConfirm}
                className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 dark:border-navy-600 dark:text-slate-200 dark:hover:bg-navy-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteDocument(deleteConfirm.doc)}
                disabled={deleting}
                className="rounded-2xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default Upload;
